import React from "react";
import "./App.css";

import AuthProvider from "./hooks/AuthProvider";
import RedirectToLogin from "./RedirectToLogin";
import RequireAuth from "./RequireAuth";

import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./hooks/AuthProvider";

import { Login } from "./Pages/Login";
import { Devices } from "./Pages/Devices";
import { DeviceDetails } from "./Pages/DeviceDetails";
import { DeviceStatusLog } from "./Pages/DeviceStatusLog";
import { Configuration } from "./Pages/Configuration";
import { Organizations } from "./Pages/Organizations";
import { EditOrganization } from "./Pages/EditOrganization";
import { OrganizationDetails } from "./Pages/OrganizationDetails";
import { Templates } from "./Pages/Templates";
import { Dashboards } from "./Pages/Dashboards";
import { DashboardDetails } from "./Pages/DashboardDetails";
import { TemplateDetails } from "./Pages/TemplateDetails";
import { TemplateExamples } from "./Pages/TemplateExamples";
import { TemplateExampleDetails } from "./Pages/TemplateExampleDetails";
import { DashboardExamples } from "./Pages/DashboardExamples";
import { DashboardExampleDetails } from "./Pages/DashboardExampleDetails";
import { EditDashboard } from "./Pages/EditDashboard";
import { SamaOTA } from "./Pages/Shipments";
import { CreateShipment } from "./Pages/CreateShipment";
import { ShipmentDetails } from "./Pages/ShipmentDetails";
import { Notifications } from "./Pages/Notifications";
import Weather from "./Pages/Weather";
import { Automations, NewAutomation, AutomationDetails } from "./Pages/Automations";
import { Locations } from "./Pages/Locations";
import { Administration } from "./Pages/Administration";
import { Users } from "./Pages/Users";
import { Subscriptions } from "./Pages/Subscriptions";
import SubscriptionDetails from "./Pages/SubscriptionDetails";
import { UserDetails } from "./Pages/UserDetails";
import { EditUser } from "./Pages/EditUser";
import { Permissions } from "./Pages/Permissions";
import { Roles } from "./Pages/Roles";
import { Payment } from "./Pages/Payment";
import { Signup } from "./Pages/SignUp";
import { VerifyCode } from "./Pages/VerifyEmail";
import { AssetCards } from "./Pages/AssetCards";
import Plans from "./Pages/Plans";
import DevicePlanLifecycle from "./Pages/DevicePlanLifecycle";
import SystemNotifications from "./Pages/SystemNotifications";
import SystemConfigurations from "./Pages/SystemConfigurations";
import { FontSizeProvider } from "./context/FontSizeContext";
import { AssetCardDetails } from "./Pages/AssetCardDetails";
import { AssetHistoryPage } from "./Pages/AssetHistoryPage";
import { ForgotPassword } from "./Pages/ForgotPassword";
import { VerifyResetCode } from "./Pages/VerifyResetCode";
import { ResetPassword } from "./Pages/ResetPassword";
import { AcceptInvitePassword } from "./Pages/AcceptInvitePassword";
import MobileRedirector from "./MobileRedirector";
import { AuditLogs } from "./Pages/AuditLogs";
import ForceChangePassword from "./Pages/ForceChangePassword";
import { ContactUs } from "./Pages/ContactUs";
import { DialogProvider } from "./Components/DialogProvider";
import WebNotificationManager from "./Components/WebNotificationManager";

import Spinner from "./Components/Spinner";
import { UserHealth } from "./Pages/userHealth/UsersHealth";
import { MyHealth } from "./Pages/userHealth/MyHealth";
import { MedicalDevices } from "./Pages/userHealth/MedicalDevices";
import { UserHealthDetails } from "./Pages/userHealth/UserHealthDetails";
import { UserHealthDashboard } from "./Pages/userHealth/UserHealthDashboard";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
function ContactRoute() {
  const { authed, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ width: "100vw", height: "100vh", display: "grid", placeItems: "center" }}>
        <Spinner />
      </div>
    );
  }

  if (authed) {
    return (
      <RequireAuth>
        <ContactUs embedded />
      </RequireAuth>
    );
  }

  return <ContactUs />;
}

function AuthenticatedNotificationManager() {
  const { authed, user } = useAuth();
  return <WebNotificationManager authed={authed} user={user} />;
}

function PublicRoute({ children }) {
  const { authed, loading, passwordExpired, resetPasswordRequired, permissions } = useAuth();

  if (loading) {
    return (
      <div style={{ width: "100vw", height: "100vh", display: "grid", placeItems: "center" }}>
        <Spinner />
      </div>
    );
  }

  if (authed) {
    const hasAnyPermission =
      permissions &&
      typeof permissions === "object" &&
      Object.values(permissions).some(Boolean);

    return (
      <Navigate
        to={
          resetPasswordRequired
            ? "/reset-password"
            : passwordExpired
              ? "/force-change-password"
              : hasAnyPermission
                ? "/devices"
                : "/contact-us"
        }
        replace
      />
    );
  }

  return children;
}

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>

      <div>
        <Router basename="/iot">
          <MobileRedirector />
          <DialogProvider>
            <AuthProvider>
              <FontSizeProvider>
                <AuthenticatedNotificationManager />
                <Routes>
                  <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                  <Route path="/contact-us" element={<ContactRoute />} />
                  <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
                  <Route path="/verify-code" element={<PublicRoute><VerifyCode /></PublicRoute>} />
                  <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
                  <Route path="/verify-reset-code" element={<PublicRoute><VerifyResetCode /></PublicRoute>} />
                  <Route path="/accept-invite" element={<PublicRoute><AcceptInvitePassword /></PublicRoute>} />

                  <Route path="/" element={<RedirectToLogin />} />

                  <Route element={<RequireAuth minimalLayout />}>
                    <Route path="/reset-password" element={<ResetPassword />} />
                  </Route>

                  <Route element={<RequireAuth />}>
                    <Route path="/force-change-password" element={<ForceChangePassword />} />
                  </Route>

                  <Route element={<RequireAuth />}>
                    <Route path="/devices" element={<Devices />} />
                    <Route path="/devices/details/:id" element={<DeviceDetails />} />
                    <Route path="/devices/status-log/:id" element={<DeviceStatusLog />} />

                    <Route path="/configuration" element={<Configuration />} />
                    <Route path="/configuration/system" element={<SystemConfigurations />} />
                    <Route path="/configuration/templates" element={<Templates />} />
                    <Route path="/configuration/templates/details/:id" element={<TemplateDetails />} />
                    <Route path="/configuration/template-examples" element={<TemplateExamples />} />
                    <Route path="/configuration/template-examples/details/:id" element={<TemplateExampleDetails />} />
                    <Route path="/configuration/dashboard-examples" element={<DashboardExamples />} />
                    <Route path="/configuration/dashboard-examples/details/:id" element={<DashboardExampleDetails />} />

                    <Route path="/dashboards" element={<Dashboards />} />
                    <Route path="/dashboards/details/:id" element={<DashboardDetails />} />
                    <Route path="/dashboards/edit-dashboard/:id" element={<EditDashboard />} />

                    <Route path="/configuration/ota" element={<SamaOTA />} />
                    <Route path="/configuration/ota/shipment/:id" element={<ShipmentDetails />} />
                    <Route path="/configuration/ota/create" element={<CreateShipment />} />

                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/weather" element={<Weather />} />

                    <Route path="/automations" element={<Automations />} />
                    <Route path="/automations/new" element={<NewAutomation />} />
                    <Route path="/automations/details/:id" element={<AutomationDetails />} />

                    <Route path="/locations" element={<Locations />} />

                    <Route path="/administration" element={<Administration />} />
                    <Route path="/administration/organizations" element={<Organizations />} />
                    <Route path="/administration/organizations/edit/:id" element={<EditOrganization />} />
                    <Route path="/administration/organizations/details/:id" element={<OrganizationDetails />} />

                    <Route path="/administration/users" element={<Users />} />
                    <Route path="/administration/users/edit/:id" element={<EditUser />} />
                    <Route path="/administration/users/details/:id" element={<UserDetails />} />

                    <Route path="/administration/subscriptions" element={<Subscriptions />} />
                    <Route path="/administration/subscriptions/details/:id" element={<SubscriptionDetails />} />
                    <Route path="/administration/plans" element={<Plans />} />
                    <Route path="/administration/device-plans" element={<DevicePlanLifecycle />} />
                    <Route path="/administration/system-notifications" element={<SystemNotifications />} />
                    <Route path="/administration/payment" element={<Payment />} />

                    <Route path="/administration/permissions" element={<Permissions />} />
                    <Route path="/administration/roles" element={<Roles />} />
                    <Route path="/administration/audit-logs" element={<AuditLogs />} />

                    <Route path="/asset-card" element={<AssetCards />} />
                    <Route path="/asset-card/details/:id" element={<AssetCardDetails />} />
                    <Route path="/asset-card/details/:id/history" element={<AssetHistoryPage />} />

                    <Route path="/users-health" element={<UserHealth />} />
                    <Route path="/users-health-dashboard" element={<UserHealthDashboard />} />
                    <Route path="/my-health" element={<MyHealth />} />
                    <Route path="/medical-devices" element={<MedicalDevices />} />
                    <Route path="/my-health/details/:id" element={<UserHealthDetails />} />
                    <Route path="/users-health/details/:id" element={<UserHealthDetails />} />

                  </Route>
                </Routes>
              </FontSizeProvider>
            </AuthProvider>
          </DialogProvider>
        </Router>
      </div>
    </LocalizationProvider>

  );
}

export default App;
